import type { ChangeEventHandler } from "react";
import { useFormContext } from "react-hook-form";

import { ProductPackageIcon } from "@jti/brand-icons/product-package";
import { ProductPackageMultipleIcon } from "@jti/brand-icons/product-package-multiple";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { SticksProductWithAggregations } from "@product-details/containers/SticksProductDetailsContainer/types";
import type { SticksFormValues } from "@product-details/forms/SticksForm/SticksForm.types";

import styles from "../../SticksForm.module.scss";

const PACKAGING_PREFERENCE_GROUP_NAME: keyof SticksFormValues = "packagingPreference";

type PackagingPreferenceRadioGroupProps = {
  currentProduct: SticksProductWithAggregations;
  step: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export const PackagingPreferenceGroup = ({ currentProduct, step, onChange }: PackagingPreferenceRadioGroupProps) => {
  const { __ } = useTranslations();

  const { register, watch } = useFormContext<SticksFormValues>();

  const orderFrequency = watch("orderFrequency");

  const registerProps = register(PACKAGING_PREFERENCE_GROUP_NAME, { onChange });

  return (
    <ConsumablesFormStepSection
      title={
        <>
          <strong className={styles.stepCountLabel}>{__(`sticksForm.step.order_text.${step}`)}</strong>{" "}
          {__("sticksForm.step.preferred_format.title")}
        </>
      }
      className={styles.stepSection}
      data-testid="packaging-options"
    >
      <RadioCard
        groupName={PACKAGING_PREFERENCE_GROUP_NAME}
        value="single_packs"
        title={__("sticksForm.preferred_format.pack_one_flavor")}
        subtitle={__("sticksForm.preferred_format.pack_one_flavor.subtitle")}
        icon={<ProductPackageIcon width="22px" height="23px" />}
        isUnavailable={orderFrequency === "subscription"}
        {...registerProps}
      />
      <RadioCard
        groupName={PACKAGING_PREFERENCE_GROUP_NAME}
        value="cartons"
        title={__("sticksForm.preferred_format.carton_one_flavor")}
        subtitle={__("sticksForm.preferred_format.carton_one_flavor.subtitle")}
        icon={<ProductPackageIcon width="22px" height="26.92px" />}
        isDisabled={!currentProduct.aggregations?.[orderFrequency]?.cartons?.sku}
        {...registerProps}
      />
      <RadioCard
        groupName={PACKAGING_PREFERENCE_GROUP_NAME}
        value="mix_n_match"
        title={__("sticksForm.preferred_format.carton_mix_and_match")}
        subtitle={__("sticksForm.preferred_format.carton_mix_and_match.subtitle")}
        icon={<ProductPackageMultipleIcon width="22.93px" height="28.12px" />}
        isDisabled={!currentProduct.aggregations?.[orderFrequency]?.mix_n_match?.sku}
        {...registerProps}
      />
    </ConsumablesFormStepSection>
  );
};
